import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  AssetBookingRequest,
  BookableAssetCreateRequest,
  BookableAssetUpdateBookingsToBeCancelledRequest,
  BookableAssetUpdateRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { downloadFile } from "@/helpers/file-parse";
import { isHttpError } from "@/helpers/Network/errors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

import { useBookableAssetsQueries } from "./queryOptions";

const useAddAsset = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { data: BookableAssetCreateRequest }) => api.postBookableAssetsV1(payload.data),
    onSuccess: async () => {
      showFlashToast({
        variant: "success",
        message: t("page.bookings.create-or-edit-asset.create.notification.success"),
      });

      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BOOKINGS_ASSETS_INFINITE(projectId),
      });
    },
    onError: () =>
      showFlashToast({
        variant: "alert",
        message: t("page.bookings.create-or-edit-asset.create.notification.error"),
      }),
  });
};

const useUpdateAsset = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { assetId: string; data: BookableAssetUpdateRequest }) =>
      api.putBookableAssetsV1(payload.assetId, payload.data),
    onSuccess: async (_, payload) => {
      showFlashToast({
        variant: "success",
        message: t("page.bookings.create-or-edit-asset.edit.notification.success"),
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BOOKINGS_ASSETS_INFINITE(projectId),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BOOKINGS_ASSET_DETAILS(projectId, payload.assetId),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BOOKINGS_ASSET_SLOTS(projectId, payload.assetId),
        }),
      ]);
    },
    onError: () =>
      showFlashToast({
        variant: "alert",
        message: t("page.bookings.create-or-edit-asset.edit.notification.error"),
      }),
  });
};

const useDeleteBooking = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId, assetId, reason }: { bookingId: string; assetId: string; reason?: string }) =>
      api.deleteBookableAssetsBookingsByIdV1(bookingId, assetId, { reason }).then((x) => x.data),
    onSuccess: (_, payload) => {
      showFlashToast({
        variant: "success",
        message: t("page.bookings.asset-detail.decline-booking.success"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_SLOTS(projectId, payload.assetId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BOOKINGS_ASSET_BOOKINGS(projectId, payload.assetId),
      });

      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BOOKINGS_USER_RESERVATIONS(projectId),
      });

      queryClient.removeQueries({
        queryKey: QUERY_KEYS.BOOKINGS_RESERVATION_DETAILS(projectId, payload.assetId, payload.bookingId),
      });
    },
    onError: () =>
      showFlashToast({
        variant: "alert",
        message: t("page.bookings.asset-detail.decline-booking.error"),
      }),
  });
};

const useExportAllBookings = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ includeHistory }: { fileName: string; includeHistory: boolean }) =>
      api.getBookableAssetsBookingsProjectExportV1({
        includeHistory,
      }),
    onSuccess: async (data, payload) => {
      const blob = await data.blob();
      downloadFile(blob, payload.fileName);
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.bookings.list-assets.export.error"),
      });
    },
  });
};

const useCheckExternalSyncStatus = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { assetId: string }) => api.postBookableAssetsOutlookTestV1(payload.assetId),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BOOKINGS_ASSET_DETAILS(projectId, payload.assetId),
      });
    },
  });
};

const useFetchAssetStatistics = () => {
  const api = useApi();

  return useMutation({
    mutationFn: (assetId: string) => api.getBookableAssetsStatisticsV1(assetId).then((x) => x.data),
  });
};

const useDeleteAsset = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assetId: string) => api.deleteBookableAssetsByIdV1(assetId).then((x) => x.data),
    onSuccess: (_, assetId) => {
      showFlashToast({
        variant: "success",
        message: t("page.bookings.delete-asset.notification.success"),
      });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_DETAILS(projectId, assetId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSETS(projectId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BOOKINGS_ASSETS_INFINITE(projectId),
      });
    },
    onError: () =>
      showFlashToast({
        variant: "alert",
        message: t("page.bookings.delete-asset.notification.error"),
      }),
  });
};

const useCreateBooking = (assetId: string, bookingId?: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (req: AssetBookingRequest) => api.postBookableAssetsBookingsV1(assetId, req).then((x) => x.data),
    onError: (e) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_SLOTS(projectId, assetId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_BOOKINGS(projectId, assetId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_STATISTICS(projectId, assetId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_USER_RESERVATIONS(projectId) });
      if (bookingId) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BOOKINGS_RESERVATION_DETAILS(projectId, assetId, bookingId),
        });
      }
      if (isHttpError(e) && e.status === 409) {
        showFlashToast({
          variant: "alert",
          message: t("page.bookings.book-asset.error.already-booked"),
          "data-testid": "booking-submit-error",
        });
      } else {
        showFlashToast({
          variant: "alert",
          message: t("page.bookings.book-asset.error.generic"),
          "data-testid": "booking-submit-error",
        });
      }
    },
  });
};

const useEditBooking = (assetId: string, bookingId: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const bookingQuery = useBookableAssetsQueries().bookingDetails({ assetId, bookingId });

  return useMutation({
    mutationFn: (req: AssetBookingRequest) =>
      api.putBookableAssetsBookingsV1(bookingId, assetId, req).then((x) => x.data),
    onError: async (e) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_SLOTS(projectId, assetId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_BOOKINGS(projectId, assetId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_ASSET_STATISTICS(projectId, assetId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BOOKINGS_USER_RESERVATIONS(projectId) });
      await queryClient.invalidateQueries({ queryKey: bookingQuery.queryKey });
      const booking = queryClient.getQueryData(bookingQuery.queryKey);
      if (isHttpError(e) && e.status === 409) {
        showFlashToast({
          variant: "alert",
          message: t("page.bookings.book-asset.error.already-booked"),
          "data-testid": "booking-submit-error",
        });
      } else if (isHttpError(e) && e.status === 400 && booking && !booking.data.canEdit) {
        showFlashToast({
          variant: "alert",
          message: t("page.bookings.edit-booking.unavailable"),
          "data-testid": "booking-submit-error",
        });
      } else {
        showFlashToast({
          variant: "alert",
          message: t("page.bookings.book-asset.error.generic"),
          "data-testid": "booking-submit-error",
        });
      }
    },
  });
};

const useGetBookingsToBeCancelled = () => {
  const api = useApi();

  return useMutation({
    mutationFn: (payload: { id: string; data: BookableAssetUpdateBookingsToBeCancelledRequest }) =>
      api.postBookableAssetsBookingsToBeCancelledV1(payload.id, payload.data).then((x) => x.data),
  });
};

const useExportAssetBookings = (assetId: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ includeHistory }: { includeHistory: boolean; filename: string }) =>
      api.getBookableAssetsBookingsExportV1(assetId, { includeHistory }),
    onSuccess: async (data, payload) => {
      const blob = await data.blob();
      downloadFile(blob, payload.filename);
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.bookings.asset-detail.export.error") });
    },
  });
};

export const bookableAssetMutations = {
  useDeleteBooking,
  useExportAllBookings,
  useDeleteAsset,
  useAddAsset,
  useUpdateAsset,
  useCheckExternalSyncStatus,
  useFetchAssetStatistics,
  useCreateBooking,
  useEditBooking,
  useGetBookingsToBeCancelled,
  useExportAssetBookings,
};
